// ** Modules
import React from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';

// ** UI
import Box from '@mui/material/Box';
import List from '@mui/material/List';

// ** Icons
import IconButton from '@mui/material/IconButton';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';

// ** Components
import Link from '@/components/common/Link';
import CustomDrawer from '@/components/layout/vertical/navMenu/components/CustomDrawer';
import VerticalNavItems from './VerticalNavItems';
import VerticalNavHeader from './VerticalNavHeader';

// ** Theme
import themeConfig from '@/theme/themeConfig';

// ** Types
import type { AppContext } from '@/types/contextTypes';
import type { LayoutProps } from '@/types/layoutProps';

type VerticalLayout = NonNullable<LayoutProps['verticalLayoutProps']>;
type NavMenu = NonNullable<VerticalLayout['navMenu']>;
type Props = {
	host: AppContext['host'];
	navWidth: number;
	navVisible: boolean;
	collapsedNavWidth: number;
	hidden: LayoutProps['hidden'];
	navigationBorderWidth: number;
	toggleNavVisibility: () => void;
	settings: LayoutProps['settings'];
	setNavVisible: (value: boolean) => void;
	saveSettings: LayoutProps['saveSettings'];
	verticalNavMenuContent: NavMenu['content'];
	navMenuBranding: NavMenu['branding'];
	menuLockedIcon: NavMenu['lockedIcon'];
	verticalNavItems: NavMenu['navItems'];
	navMenuProps: NavMenu['componentProps'];
	menuUnlockedIcon: NavMenu['unlockedIcon'];
	afterVerticalNavMenuContent: NavMenu['afterContent'];
	beforeVerticalNavMenuContent: NavMenu['beforeContent'];
};

// type customHTMLElement = HTMLElement & {
// 	_getBoundingClientRect: () => DOMRect;
// };

// Styled Components
// const StyledBoxForShadow = styled(Box)<BoxProps>({
// 	top: 60,
// 	left: -8,
// 	zIndex: 2,
// 	height: 75,
// 	display: 'none',
// 	position: 'absolute',
// 	pointerEvents: 'none',
// 	width: 'calc(100% + 15px)',
// 	'&.d-block': {
// 		display: 'block'
// 	}
// });

const VerticalNavigation = (props: Props) => {
	// Props
	const {
		// hidden,
		afterVerticalNavMenuContent,
		beforeVerticalNavMenuContent,
		verticalNavMenuContent: userVerticalNavMenuContent,
		settings
	} = props;
	const { navcollapsed } = settings;

	// States
	const [navHover, setNavHover] = React.useState<boolean>(false);
	const [groupActive, setGroupActive] = React.useState<string[]>([]);
	const [currentActiveGroup, setCurrentActiveGroup] = React.useState<string[]>([]);

	// Ref
	// const shadowRef = React.useRef(null);

	const { afterVerticalNavMenuContentPosition, beforeVerticalNavMenuContentPosition } = themeConfig;

	const navMenuContentProps = {
		...props,
		navHover,
		groupActive,
		setGroupActive,
		currentActiveGroup,
		setCurrentActiveGroup
	};

	// Fix Navigation InfiniteScroll
	// const handleInfiniteScroll = (ref: HTMLElement | null) => {
	// 	if (ref && !(ref as any)._getBoundingClientRect) {
	// 		(ref as any)._getBoundingClientRect = ref.getBoundingClientRect;
	// 		ref.getBoundingClientRect = () => {
	// 			const original = (ref as any)._getBoundingClientRect();
	// 			return { ...original, height: Math.floor(original.height) };
	// 		};
	// 	}
	// };

	// Scroll Menu
	// const scrollMenu = (container: any) => {
	// 	if (
	// 		beforeVerticalNavMenuContentPosition === 'static' ||
	// 		!beforeVerticalNavMenuContent
	// 	) {
	// 		container = hidden ? container.target : container;
	// 		if (shadowRef && container.scrollTop > 0) {
	// 			if (!shadowRef.current.classList.contains('scrolled')) {
	// 				shadowRef.current.classList.add('scrolled');
	// 			}
	// 		} else {
	// 			shadowRef.current.classList.remove('scrolled');
	// 		}
	// 	}
	// };

	return (
		<CustomDrawer {...props} navHover={navHover} setNavHover={setNavHover}>
			<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
				{/* Header */}
				<VerticalNavHeader navHover={navHover} {...props} />

				{/* Optional before content (fixed) */}
				{beforeVerticalNavMenuContent &&
					beforeVerticalNavMenuContentPosition === 'fixed' &&
					beforeVerticalNavMenuContent(navMenuContentProps)}

				{/* Scrollable Menu */}
				<Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
					<PerfectScrollbar options={{ wheelPropagation: false }} style={{ height: '100%' }}>
						<Box sx={{ display: 'flex', flexDirection: 'column' }}>
							{/* Menu content */}
							{beforeVerticalNavMenuContent &&
								beforeVerticalNavMenuContentPosition === 'static' &&
								beforeVerticalNavMenuContent(navMenuContentProps)}

							{userVerticalNavMenuContent ? (
								userVerticalNavMenuContent(navMenuContentProps)
							) : (
								<List className="nav-items" sx={{ pt: 0 }}>
									<VerticalNavItems
										navHover={navHover}
										groupActive={groupActive}
										setGroupActive={setGroupActive}
										currentActiveGroup={currentActiveGroup}
										setCurrentActiveGroup={setCurrentActiveGroup}
										{...props}
									/>
								</List>
							)}

							{afterVerticalNavMenuContent &&
								afterVerticalNavMenuContentPosition === 'static' &&
								afterVerticalNavMenuContent(navMenuContentProps)}
						</Box>
					</PerfectScrollbar>
				</Box>

				{/* Footer (social links, fixed at bottom) */}
				<Box
					sx={{
						display: 'flex',
						flexDirection: navHover || !navcollapsed ? 'row' : 'column',
						alignItems: 'center',
						justifyContent: navHover || !navcollapsed ? 'flex-start' : 'center',
						gap: 2,
						p: navHover || !navcollapsed ? 2 : 0,
						borderTop: '1px solid #ccc',
						flexShrink: 0,
						backgroundColor: 'background.paper'
					}}
				>
					<Link href="https://facebook.com" target="_blank" passHref>
						<IconButton>
							<FacebookIcon color="primary" />
						</IconButton>
					</Link>
					<Link href="https://instagram.com" target="_blank" passHref>
						<IconButton>
							<InstagramIcon color="primary" />
						</IconButton>
					</Link>
					<Link href="https://youtube.com" target="_blank" passHref>
						<IconButton>
							<YouTubeIcon color="primary" />
						</IconButton>
					</Link>
					<Link href="https://linkedin.com" target="_blank" passHref>
						<IconButton>
							<LinkedInIcon color="primary" />
						</IconButton>
					</Link>
				</Box>
			</Box>
		</CustomDrawer>
	);
};

export default VerticalNavigation;
